// Typed client for POST /api/medications/ocr. Mirrors the server-side
// OcrCandidate shape from packages/api/src/services/ocr.ts.

import { useCallback } from 'react';
import { env } from '../env';
import { useSession } from '../hooks/SessionProvider';
import { UnauthenticatedError } from '../hooks/useAuthedFetch';

export interface OcrCandidate {
  rxcui: string;
  displayName: string;
  ingredientRxcui: string | null;
  ingredientName: string | null;
  score: number;
}

export interface OcrDosingHint {
  text: string | null;
  slots: Array<'morning' | 'noon' | 'evening' | 'bedtime' | 'asneeded'>;
  asNeeded: boolean;
  kind: 'prescription' | 'otc' | 'supplement';
  strength: string | null;
  form: string | null;
}

export interface OcrFdaLabel {
  brandName: string | null;
  genericName: string | null;
  manufacturer: string | null;
  ndc: string | null;
}

export interface OcrGeminiVision {
  name: string;
  strength: string | null;
  form: string | null;
  kind: 'prescription' | 'otc' | 'supplement';
  ndc: string | null;
  confidence: 'high' | 'medium' | 'low';
}

export interface OcrResponse {
  rawText: string;
  parsedCandidateTexts: string[];
  candidates: OcrCandidate[];
  geminiName?: string | null;
  geminiVision?: OcrGeminiVision | null;
  confidence?: 'high' | 'medium' | 'low';
  dosingHint?: OcrDosingHint | null;
  fdaLabel?: OcrFdaLabel | null;
}

interface ApiSuccess<T> {
  ok: true;
  data: T;
}
interface ApiError {
  ok: false;
  error: { code: string; message: string };
}

export function useOcrUpload(): (images: Blob | Blob[]) => Promise<OcrResponse> {
  // OCR upload uses multipart/form-data, so it cannot share the JSON
  // useAuthedFetch helper. Read the medreview session JWT from the same
  // SessionProvider context useAuthedFetch uses.
  const { sessionToken, refresh } = useSession();
  return useCallback(
    async (images: Blob | Blob[]): Promise<OcrResponse> => {
      const list = Array.isArray(images) ? images : [images];
      const send = async (token: string | null): Promise<Response> => {
        if (!token) throw new UnauthenticatedError();
        const form = new FormData();
        list.forEach((img, i) => {
          form.append('image', img, `photo-${i}.jpg`);
        });
        return fetch(`${env.apiBaseUrl}/api/medications/ocr`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          body: form,
        });
      };
      let token = sessionToken;
      if (!token) token = await refresh();
      let res = await send(token);
      if (res.status === 401) {
        const fresh = await refresh();
        if (!fresh) throw new UnauthenticatedError();
        res = await send(fresh);
      }
      const body = (await res.json()) as ApiSuccess<OcrResponse> | ApiError;
      if (!body.ok) throw new Error(`${body.error.code}: ${body.error.message}`);
      return body.data;
    },
    [sessionToken, refresh],
  );
}
